import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import ConfirmDialog from '../common/ConfirmDialog';

type TranscriptMessageStyleProps = {
  type: TranscriptTypes;
};

const useStyles = makeStyles<Theme, TranscriptMessageStyleProps>(({}) => ({
  transcriptMessageClass: {
    fontSize: '14px',
    lineHeight: '20px',
    paddingBottom: '4px',
    display: 'flex',
    color: ({ type }) =>
      `${type === TranscriptTypes.AI ? '#1E1E1E' : Colors.guardsmanRed}`,
  },
  sourceClass: {
    width: '1.75rem',
    fontWeight: 'bold',
  },
  contentClass: {
    width: 'calc(100% - 3.75rem)',
  },
  wordSpanClass: {
    display: 'inline-block',
    padding: '0 2px',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: '#fff59d',
      cursor: 'pointer',
    },
  },
  langIndicatorClass: {
    width: '2rem',
    textAlign: 'center',
    paddingRight: '4px',
  },
  secondLangIndicatorClass: {
    backgroundColor: '#0797EA',
    color: '#FFFFFF',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  secondLangClass: {
    fontStyle: 'italic',
  },
}));

interface ITranscriptMessage {
  text: string;
  type: TranscriptTypes;
  secondLangText?: string;
}

export const TranscriptMessage = ({
  text = '•',
  type,
  secondLangText = '',
}: ITranscriptMessage) => {
  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;

  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const {
    transcriptMessageClass,
    sourceClass,
    contentClass,
    langIndicatorClass,
    secondLangClass,
    secondLangIndicatorClass,
    wordSpanClass,
  } = useStyles({
    type,
  });

  const lastClickRef = useRef<{ word: string; time: number } | null>(null);

  const words = useMemo(() => {
    if (!text) {
      return [] as string[];
    }
    return text.split(/\s+/).filter((w) => w.length > 0);
  }, [text]);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    word: string;
  }>({ open: false, word: '' });

  const openConfirmDialog = useCallback(
    (wordClickedRaw: string | undefined) => {
      const wordClicked =
        !wordClickedRaw || wordClickedRaw === '/silence'
          ? 'ASR MISSING'
          : wordClickedRaw;

      const now = Date.now();
      if (
        lastClickRef.current &&
        lastClickRef.current.word === wordClicked &&
        now - lastClickRef.current.time < 500
      ) {
        return;
      }
      lastClickRef.current = { word: wordClicked, time: now };

      setConfirmDialog({ open: true, word: wordClicked });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    const wordClicked = confirmDialog.word;

    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked,
    });

    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, [
    confirmDialog.word,
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
  ]);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const handleContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const target = event.target as HTMLElement | null;
      const wordFromTarget = target?.dataset?.word;
      if (!wordFromTarget) {
        return;
      }

      openConfirmDialog(wordFromTarget);
    },
    [openConfirmDialog]
  );

  return (
    <>
      <div className={transcriptMessageClass}>
        <div className={langIndicatorClass}>
          <div className={secondLangIndicatorClass}>
            {secondLangText ? 'S' : ''}
          </div>
        </div>
        <div className={sourceClass}>{type}:&nbsp;</div>
        <div className={contentClass} onClick={handleContentClick}>
          <>
            {words.length === 0
              ? text
              : words.map((word, index) => (
                  <span
                    key={`${word}-${index}`}
                    data-word={word}
                    className={wordSpanClass}
                  >
                    {index > 0 ? ' ' : ''}
                    {word}
                  </span>
                ))}
            {secondLangText ? (
              <span className={secondLangClass}>
                {`     | ${secondLangText}`}
              </span>
            ) : null}
          </>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDialog.open}
        content={`Send ASR incorrect event for word: "${
          confirmDialog.word || 'ASR MISSING'
        }"?`}
        onClose={handleCloseDialog}
        onConfirm={handleConfirm}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </>
  );
};
